import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes, css } from "styled-components";

const ANIMATION_MS = 700;
const PULSE_DELAY_MS = 460;
const PULSE_DURATION_MS = 520;

const FlyToCollection = ({
  isActive,
  imageUrl,
  startPosition,
  imageDimensions,
  onComplete,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFlying, setIsFlying] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [animationVars, setAnimationVars] = useState({});
  const [pulseVars, setPulseVars] = useState({});

  useEffect(() => {
    if (!isActive || !imageUrl || !startPosition) return;

    // Find Mine tab element, falling back to the collection route
    let collectionTab = document.querySelector('a[href="/mine"]');

    if (!collectionTab) {
      const allElements = document.querySelectorAll("*");
      collectionTab = Array.from(allElements).find(
        (element) =>
          element.textContent?.trim() === "Mine" &&
          (element.tagName === "A" || element.querySelector("a")),
      );
      if (collectionTab && collectionTab.tagName !== "A") {
        const linkInside = collectionTab.querySelector("a");
        if (linkInside) collectionTab = linkInside;
      }
    }

    if (!collectionTab) {
      const allLinks = document.querySelectorAll("a");
      collectionTab = Array.from(allLinks).find(
        (link) =>
          link.href?.includes("/mine") || link.getAttribute("href") === "/mine",
      );
    }

    if (!collectionTab) {
      onComplete?.();
      return;
    }

    // If user is scrolled down, smoothly bring them to top so the eye
    // can follow the flying image up to the Mine tab in the header.
    if (typeof window !== "undefined" && window.scrollY > 8) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }

    const collectionRect = collectionTab.getBoundingClientRect();
    const startRect = startPosition;

    const imgWidth = imageDimensions?.width || 150;
    const imgHeight = imageDimensions?.height || 150;

    const startX = startRect.left + startRect.width / 2 - imgWidth / 2;
    const startY = startRect.top + startRect.height / 2 - imgHeight / 2;
    const endX = collectionRect.left + collectionRect.width / 2 - imgWidth / 2;
    const endY = collectionRect.top + collectionRect.height / 2 - imgHeight / 2;

    // Apex of the curved path: midpoint, lifted upward to give an arc
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    const distance = Math.hypot(endX - startX, endY - startY);
    const arcLift = Math.min(120, Math.max(40, distance * 0.18));
    const apexX = midX;
    const apexY = midY - arcLift;

    setAnimationVars({
      "--start-x": `${startX}px`,
      "--start-y": `${startY}px`,
      "--apex-x": `${apexX}px`,
      "--apex-y": `${apexY}px`,
      "--end-x": `${endX}px`,
      "--end-y": `${endY}px`,
      "--img-width": `${imgWidth}px`,
      "--img-height": `${imgHeight}px`,
    });

    setPulseVars({
      "--pulse-x": `${collectionRect.left + collectionRect.width / 2}px`,
      "--pulse-y": `${collectionRect.top + collectionRect.height / 2}px`,
    });

    setIsVisible(true);

    const flyTimer = setTimeout(() => setIsFlying(true), 30);
    const pulseTimer = setTimeout(() => setIsPulsing(true), PULSE_DELAY_MS);
    const pulseEndTimer = setTimeout(
      () => setIsPulsing(false),
      PULSE_DELAY_MS + PULSE_DURATION_MS,
    );
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      setIsFlying(false);
      onComplete?.();
    }, ANIMATION_MS + 80);

    return () => {
      clearTimeout(flyTimer);
      clearTimeout(pulseTimer);
      clearTimeout(pulseEndTimer);
      clearTimeout(hideTimer);
    };
  }, [isActive, imageUrl, startPosition, imageDimensions, onComplete]);

  if (!isVisible || !imageUrl) return null;

  return createPortal(
    <>
      <FlyingImage
        src={imageUrl}
        alt=""
        style={animationVars}
        $flying={isFlying}
      />
      {isPulsing && <TabPulse style={pulseVars} />}
    </>,
    document.body,
  );
};

export default FlyToCollection;

// ─── Animation: black-hole suction along an arc ──────────────────────────────

const blackHoleSuck = keyframes`
  0% {
    transform: translate(var(--start-x), var(--start-y)) scale(1);
    opacity: 1;
    filter: blur(0) brightness(1);
    border-radius: 12px;
  }
  10% {
    transform: translate(var(--start-x), var(--start-y)) scale(1.06);
    opacity: 1;
    filter: blur(0) brightness(1.05);
    border-radius: 12px;
  }
  60% {
    transform: translate(var(--apex-x), var(--apex-y)) scale(0.55);
    opacity: 0.95;
    filter: blur(1.5px) brightness(1.1);
    border-radius: 24px;
  }
  85% {
    transform: translate(var(--end-x), var(--end-y)) scale(0.18);
    opacity: 0.5;
    filter: blur(3px) brightness(1.3);
    border-radius: 50%;
  }
  100% {
    transform: translate(var(--end-x), var(--end-y)) scale(0);
    opacity: 0;
    filter: blur(8px) brightness(1.6);
    border-radius: 50%;
  }
`;

const tabPulseAnim = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.4);
    opacity: 0;
  }
  20% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.6);
    opacity: 0;
  }
`;

const FlyingImage = styled.img`
  position: fixed;
  z-index: 99999;
  width: var(--img-width, 150px);
  height: var(--img-height, 150px);
  object-fit: cover;
  border-radius: 12px;
  pointer-events: none;
  top: 0;
  left: 0;
  transform: translate(var(--start-x, 0px), var(--start-y, 0px)) scale(1);
  opacity: 1;
  will-change: transform, opacity, filter, border-radius;

  ${(p) =>
    p.$flying &&
    css`
      animation: ${blackHoleSuck} ${ANIMATION_MS}ms
        cubic-bezier(0.55, 0.05, 0.65, 1) forwards;
    `}
`;

const TabPulse = styled.div`
  position: fixed;
  top: var(--pulse-y, 0px);
  left: var(--pulse-x, 0px);
  width: 64px;
  height: 64px;
  border-radius: 50%;
  pointer-events: none;
  z-index: 99998;
  background: radial-gradient(
    circle,
    rgba(var(--color-brand-300-rgb), 0.55) 0%,
    rgba(var(--color-brand-400-rgb), 0.35) 35%,
    rgba(var(--color-brand-500-rgb), 0) 70%
  );
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.4);
  box-shadow:
    0 0 18px rgba(var(--color-brand-400-rgb), 0.55),
    0 0 36px rgba(var(--color-brand-500-rgb), 0.35);
  transform: translate(-50%, -50%) scale(0);
  opacity: 0;
  animation: ${tabPulseAnim} ${PULSE_DURATION_MS}ms ease-out forwards;
`;
